// Re-export everything from the Orval-generated React Query hooks + types.
// This barrel is the package entry point (see package.json "main").
// The generated file is never the main directly so that the non-generated
// axios-instance exports below are always available alongside the codegen output.
export * from './generated/index.js';

// Manual exports: axios instance helpers used by App.tsx and portal pages.
export {
  axiosInstance,
  customInstance,
  setAuthToken,
  setTokenProvider,
  setClaimModeHeader,
  makePortalInstance,
  setReviewerTokenProvider,
  setReviewerClaimId,
  getPortalRequestInit,
  setActAsToken,
  getActAsToken,
  setOnActAsEnded,
} from './axios-instance.js';
export type { PortalInstanceMode, PortalInstanceOptions } from './axios-instance.js';

// ---------------------------------------------------------------------------
// Adapter hooks — wrap the Orval-generated hooks to match the calling
// conventions used by the page components.  Orval always wraps request
// bodies as { data: Body } (and path-params+body as { id, data: Body }),
// but the pages pass flat objects or bare ID strings.
// ---------------------------------------------------------------------------

import {
  useCreateArtefact as _useCreateArtefact,
  useDeleteArtefact as _useDeleteArtefact,
  useCreateProject as _useCreateProject,
  useCreateProjectTfa as _useCreateProjectTfa,
  usePatchTfaStage as _usePatchTfaStage,
  usePatchTfaContent as _usePatchTfaContent,
  usePostTfaPanelist as _usePostTfaPanelist,
  usePostTfaDetermination as _usePostTfaDetermination,
  usePostTfaLock as _usePostTfaLock,
  usePostTfaUnlock as _usePostTfaUnlock,
  useCreateActivity as _useCreateActivity,
  useCreateCompetenceRecord as _useCreateCompetenceRecord,
  useCreateSignal as _useCreateSignal,
  usePatchAbandonedWorkCandidate as _usePatchAbandonedWorkCandidate,
  useExtractAbandonedWork as _useExtractAbandonedWork,
  useRejectTimeEntry as _useRejectTimeEntry,
  useCreateClaim as _useCreateClaim,
  useValidateClaim as _useValidateClaim,
  useLockClaim as _useLockClaim,
  useGetAccessTokens as _useGetAccessTokens,
  useCreateAccessToken as _useCreateAccessToken,
  useRevokeAccessToken as _useRevokeAccessToken,
  useCreateLink as _useCreateLink,
  useDeleteLink as _useDeleteLink,
  useAddProjectRepo as _useAddProjectRepo,
  useRemoveProjectRepo as _useRemoveProjectRepo,
  useVerifyContributor as _useVerifyContributor,
  useRegisterUser as _useRegisterUser,
} from './generated/index.js';

import type {
  CreateArtefactBody,
  CreateProjectBody,
  CreateProjectTfaBody,
  PatchTfaStageBody,
  PatchTfaContentBody,
  PostTfaPanelistBody,
  PostTfaDeterminationBody,
  CreateActivityBody,
  CreateCompetenceRecordBody,
  CreateSignalBody,
  PatchAbandonedWorkCandidateBody,
  ExtractAbandonedWork200Data,
  CreateClaimBody,
  Claim,
  GetAccessTokensParams,
  GetAccessTokens200,
  CreateAccessTokenBody,
  AccessToken,
  RevokeAccessTokenBody,
  CreateLinkBody,
  AddProjectRepoBody,
  VerifyContributorBody,
} from './generated/index.js';

// ---------------------------------------------------------------------------
// Shared helper type: make `data` required on any envelope response so that
// onSuccess callbacks typed as `(res: { data: T }) => void` satisfy TS.
// ---------------------------------------------------------------------------
type WithRequiredData<TResponse, TData> = Omit<TResponse, 'data'> & { data: TData };

// ---------------------------------------------------------------------------
// Helper to cast the `error` field from `unknown` to `Error | null` so pages
// can use it in JSX conditions without triggering TS2322.
// ---------------------------------------------------------------------------
function withTypedError<T extends { error: unknown }>(m: T): Omit<T, 'error'> & { error: Error | null } {
  return m as unknown as Omit<T, 'error'> & { error: Error | null };
}

// --- useCreateArtefact ---
// Pages: mutate({ type, title, content, projectId?, activityId? })
export function useCreateArtefact() {
  const m = _useCreateArtefact();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      body: CreateArtefactBody,
      opts?: Parameters<typeof m.mutate>[1],
    ) => m.mutate({ data: body }, opts),
    mutateAsync: (body: CreateArtefactBody) => m.mutateAsync({ data: body }),
  };
}

// --- useDeleteArtefact ---
// Pages: mutate(idString, opts?)  OR  mutate({ id }, opts?)
export function useDeleteArtefact() {
  const m = _useDeleteArtefact();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      arg: string | { id: string },
      opts?: Parameters<typeof m.mutate>[1],
    ) => m.mutate({ id: typeof arg === 'string' ? arg : arg.id }, opts),
    mutateAsync: (arg: string | { id: string }) =>
      m.mutateAsync({ id: typeof arg === 'string' ? arg : arg.id }),
  };
}

// --- useCreateProject ---
// Pages: mutate({ name, incomeYear, startDate, endDate?, description? })
export function useCreateProject() {
  const m = _useCreateProject();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      body: CreateProjectBody,
      opts?: Parameters<typeof m.mutate>[1],
    ) => m.mutate({ data: body }, opts),
    mutateAsync: (body: CreateProjectBody) => m.mutateAsync({ data: body }),
  };
}

// --- useCreateProjectTfa ---
// Pages: mutate({ projectId }) with no body fields required
export function useCreateProjectTfa() {
  const m = _useCreateProjectTfa();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      args: { projectId: string } & Partial<CreateProjectTfaBody>,
      opts?: Parameters<typeof m.mutate>[1],
    ) => {
      const { projectId, ...body } = args;
      return m.mutate({ projectId, data: body as CreateProjectTfaBody }, opts);
    },
    mutateAsync: (args: { projectId: string } & Partial<CreateProjectTfaBody>) => {
      const { projectId, ...body } = args;
      return m.mutateAsync({ projectId, data: body as CreateProjectTfaBody });
    },
  };
}

// --- usePatchTfaStage ---
// Pages: mutate({ id, stage })
export function usePatchTfaStage() {
  const m = _usePatchTfaStage();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      args: { id: string } & PatchTfaStageBody,
      opts?: Parameters<typeof m.mutate>[1],
    ) => {
      const { id, ...data } = args;
      return m.mutate({ id, data: data as PatchTfaStageBody }, opts);
    },
    mutateAsync: (args: { id: string } & PatchTfaStageBody) => {
      const { id, ...data } = args;
      return m.mutateAsync({ id, data: data as PatchTfaStageBody });
    },
  };
}

// --- usePatchTfaContent ---
// Pages: mutate({ id, problemStatement? }) or mutate({ id, cotsSweepNotes? })
export function usePatchTfaContent() {
  const m = _usePatchTfaContent();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      args: { id: string } & Partial<PatchTfaContentBody>,
      opts?: Parameters<typeof m.mutate>[1],
    ) => {
      const { id, ...data } = args;
      return m.mutate({ id, data: data as PatchTfaContentBody }, opts);
    },
    mutateAsync: (args: { id: string } & Partial<PatchTfaContentBody>) => {
      const { id, ...data } = args;
      return m.mutateAsync({ id, data: data as PatchTfaContentBody });
    },
  };
}

// --- usePostTfaPanelist ---
// Pages: mutate({ id, userId })
export function usePostTfaPanelist() {
  const m = _usePostTfaPanelist();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      args: { id: string } & PostTfaPanelistBody,
      opts?: Parameters<typeof m.mutate>[1],
    ) => {
      const { id, ...data } = args;
      return m.mutate({ id, data: data as PostTfaPanelistBody }, opts);
    },
    mutateAsync: (args: { id: string } & PostTfaPanelistBody) => {
      const { id, ...data } = args;
      return m.mutateAsync({ id, data: data as PostTfaPanelistBody });
    },
  };
}

// --- usePostTfaDetermination ---
// Pages: mutate({ id, determination: string, notes: string })
// The generated body uses an enum for `determination`; we accept string and cast.
export function usePostTfaDetermination() {
  const m = _usePostTfaDetermination();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      args: { id: string; determination?: string; notes?: string | null },
      opts?: Parameters<typeof m.mutate>[1],
    ) => {
      const { id, ...body } = args;
      return m.mutate({ id, data: body as unknown as PostTfaDeterminationBody }, opts);
    },
    mutateAsync: (args: { id: string; determination?: string; notes?: string | null }) => {
      const { id, ...body } = args;
      return m.mutateAsync({ id, data: body as unknown as PostTfaDeterminationBody });
    },
  };
}

// --- usePostTfaLock ---
// Pages: mutate(idString)  — bare string
export function usePostTfaLock() {
  const m = _usePostTfaLock();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      arg: string | { id: string },
      opts?: Parameters<typeof m.mutate>[1],
    ) => m.mutate({ id: typeof arg === 'string' ? arg : arg.id }, opts),
    mutateAsync: (arg: string | { id: string }) =>
      m.mutateAsync({ id: typeof arg === 'string' ? arg : arg.id }),
  };
}

// --- usePostTfaUnlock ---
// Pages: mutate(idString) or mutate({ id })  — tenant_admin only
export function usePostTfaUnlock() {
  const m = _usePostTfaUnlock();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      arg: string | { id: string },
      opts?: Parameters<typeof m.mutate>[1],
    ) => m.mutate({ id: typeof arg === 'string' ? arg : arg.id }, opts),
    mutateAsync: (arg: string | { id: string }) =>
      m.mutateAsync({ id: typeof arg === 'string' ? arg : arg.id }),
  };
}

// --- useCreateActivity ---
// Pages: mutate({ projectId, name, description?, ipOwnership }) where ipOwnership is a string
export function useCreateActivity() {
  const m = _useCreateActivity();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      args: { projectId: string; name: string; description?: string; ipOwnership: string },
      opts?: Parameters<typeof m.mutate>[1],
    ) => {
      const { projectId, ...body } = args;
      return m.mutate({ projectId, data: body as CreateActivityBody }, opts);
    },
    mutateAsync: (args: { projectId: string; name: string; description?: string; ipOwnership: string }) => {
      const { projectId, ...body } = args;
      return m.mutateAsync({ projectId, data: body as CreateActivityBody });
    },
  };
}

// --- useCreateCompetenceRecord ---
// Pages: mutate({ activityId: id, ...fields }) — note: key is activityId not id
export function useCreateCompetenceRecord() {
  const m = _useCreateCompetenceRecord();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      args: { activityId?: string; id?: string } & Partial<CreateCompetenceRecordBody>,
      opts?: Parameters<typeof m.mutate>[1],
    ) => {
      const { activityId, id: idFallback, ...body } = args;
      const resolvedId = activityId ?? idFallback ?? '';
      return m.mutate({ id: resolvedId, data: body as CreateCompetenceRecordBody }, opts);
    },
    mutateAsync: (args: { activityId?: string; id?: string } & Partial<CreateCompetenceRecordBody>) => {
      const { activityId, id: idFallback, ...body } = args;
      const resolvedId = activityId ?? idFallback ?? '';
      return m.mutateAsync({ id: resolvedId, data: body as CreateCompetenceRecordBody });
    },
  };
}

// --- useCreateSignal ---
// Pages: mutate({ repoOwner, repoName } as ...) — flat body, no path param
export function useCreateSignal() {
  const m = _useCreateSignal();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      body: CreateSignalBody,
      opts?: Parameters<typeof m.mutate>[1],
    ) => m.mutate({ data: body }, opts),
    mutateAsync: (body: CreateSignalBody) => m.mutateAsync({ data: body }),
  };
}

// --- usePatchAbandonedWorkCandidate ---
// Pages: mutate({ id, action })
export function usePatchAbandonedWorkCandidate() {
  const m = _usePatchAbandonedWorkCandidate();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      args: { id: string } & PatchAbandonedWorkCandidateBody,
      opts?: Parameters<typeof m.mutate>[1],
    ) => {
      const { id, ...body } = args;
      return m.mutate({ id, data: body as PatchAbandonedWorkCandidateBody }, opts);
    },
    mutateAsync: (args: { id: string } & PatchAbandonedWorkCandidateBody) => {
      const { id, ...body } = args;
      return m.mutateAsync({ id, data: body as PatchAbandonedWorkCandidateBody });
    },
  };
}

// --- useExtractAbandonedWork ---
// Pages: mutateAsync(candidateId) — bare string; accesses extractionResponse.data.whatWasTried etc.
// We cast the response to make `data` non-optional so pages don't get "possibly undefined".
export function useExtractAbandonedWork() {
  const m = _useExtractAbandonedWork();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      arg: string | { id: string },
      opts?: Parameters<typeof m.mutate>[1],
    ) => m.mutate({ id: typeof arg === 'string' ? arg : arg.id }, opts),
    mutateAsync: (arg: string | { id: string }): Promise<{ data: ExtractAbandonedWork200Data }> =>
      m.mutateAsync({ id: typeof arg === 'string' ? arg : arg.id }) as Promise<{ data: ExtractAbandonedWork200Data }>,
  };
}

// --- useRejectTimeEntry ---
// Pages: mutate(idString, opts?)  — bare string
export function useRejectTimeEntry() {
  const m = _useRejectTimeEntry();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      arg: string | { id: string },
      opts?: Parameters<typeof m.mutate>[1],
    ) => m.mutate({ id: typeof arg === 'string' ? arg : arg.id }, opts),
    mutateAsync: (arg: string | { id: string }) =>
      m.mutateAsync({ id: typeof arg === 'string' ? arg : arg.id }),
  };
}

// --- useCreateClaim ---
// Pages: mutate(body, { onSuccess: (res) => navigate('/claims/' + res.data.id) })
// We narrow the onSuccess callback type so res.data is non-optional.
export function useCreateClaim() {
  const m = _useCreateClaim();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      body: CreateClaimBody,
      opts?: { onSuccess?: (res: WithRequiredData<{ data?: Claim }, Claim>) => void; onError?: (err: Error) => void },
    ) => m.mutate({ data: body }, opts as Parameters<typeof m.mutate>[1]),
    mutateAsync: (body: CreateClaimBody): Promise<WithRequiredData<{ data?: Claim }, Claim>> =>
      m.mutateAsync({ data: body }) as Promise<WithRequiredData<{ data?: Claim }, Claim>>,
  };
}

// --- useValidateClaim ---
// Pages: validate(idString, opts?)
export function useValidateClaim() {
  const m = _useValidateClaim();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      arg: string | { id: string },
      opts?: Parameters<typeof m.mutate>[1],
    ) => m.mutate({ id: typeof arg === 'string' ? arg : arg.id }, opts),
    mutateAsync: (arg: string | { id: string }) =>
      m.mutateAsync({ id: typeof arg === 'string' ? arg : arg.id }),
  };
}

// --- useLockClaim ---
// Pages call lock(id) — the ack flow is managed in claim-detail.tsx via customInstance.
export function useLockClaim() {
  const m = _useLockClaim();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (arg: string, opts?: Parameters<typeof m.mutate>[1]) =>
      m.mutate({ id: typeof arg === 'string' ? arg : arg }, opts),
    mutateAsync: (arg: string) =>
      m.mutateAsync({ id: typeof arg === 'string' ? arg : arg }),
  };
}

// --- useGetAccessTokens ---
// Pages: useGetAccessTokens(claimId) — bare string
// Explicit type parameter so `data` resolves to GetAccessTokens200 (not {}).
// Also casts `error` to Error | null so pages can use it as a JSX condition.
export function useGetAccessTokens(
  claimIdOrParams: string | GetAccessTokensParams,
  options?: Parameters<typeof _useGetAccessTokens<GetAccessTokens200>>[1],
) {
  const params: GetAccessTokensParams =
    typeof claimIdOrParams === 'string'
      ? { claimId: claimIdOrParams }
      : claimIdOrParams;
  const result = _useGetAccessTokens<GetAccessTokens200>(params, options);
  return result as typeof result & { error: Error | null };
}

// --- useCreateAccessToken ---
// Pages: mutate(body, { onSuccess: (res) => setNewToken(res.data) })
// We narrow onSuccess so res.data is non-optional AccessToken.
export function useCreateAccessToken() {
  const m = _useCreateAccessToken();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      body: CreateAccessTokenBody,
      opts?: { onSuccess?: (res: WithRequiredData<{ data?: AccessToken }, AccessToken>) => void; onError?: (err: Error) => void },
    ) => m.mutate({ data: body }, opts as Parameters<typeof m.mutate>[1]),
    mutateAsync: (body: CreateAccessTokenBody): Promise<WithRequiredData<{ data?: AccessToken }, AccessToken>> =>
      m.mutateAsync({ data: body }) as Promise<WithRequiredData<{ data?: AccessToken }, AccessToken>>,
  };
}

// --- useRevokeAccessToken ---
// Pages: mutate({ id, claimId })
export function useRevokeAccessToken() {
  const m = _useRevokeAccessToken();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      args: { id: string } & Partial<RevokeAccessTokenBody>,
      opts?: Parameters<typeof m.mutate>[1],
    ) => {
      const { id, ...body } = args;
      return m.mutate({ id, data: body as RevokeAccessTokenBody }, opts);
    },
    mutateAsync: (args: { id: string } & Partial<RevokeAccessTokenBody>) => {
      const { id, ...body } = args;
      return m.mutateAsync({ id, data: body as RevokeAccessTokenBody });
    },
  };
}

// --- useCreateLink ---
// Pages: mutate({ sourceId, targetId, linkType })
export function useCreateLink() {
  const m = _useCreateLink();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      body: CreateLinkBody,
      opts?: Parameters<typeof m.mutate>[1],
    ) => m.mutate({ data: body }, opts),
    mutateAsync: (body: CreateLinkBody) => m.mutateAsync({ data: body }),
  };
}

// --- useDeleteLink ---
// Pages: mutate(idString, opts?) OR mutate({ id }, opts?)
export function useDeleteLink() {
  const m = _useDeleteLink();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      arg: string | { id: string },
      opts?: Parameters<typeof m.mutate>[1],
    ) => m.mutate({ id: typeof arg === 'string' ? arg : arg.id }, opts),
    mutateAsync: (arg: string | { id: string }) =>
      m.mutateAsync({ id: typeof arg === 'string' ? arg : arg.id }),
  };
}

// --- useAddProjectRepo ---
// Pages: mutate({ projectId, repoOwner, repoName }) — note: id in generated = projectId
export function useAddProjectRepo() {
  const m = _useAddProjectRepo();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      args: { projectId: string; id?: string } & AddProjectRepoBody,
      opts?: Parameters<typeof m.mutate>[1],
    ) => {
      const { projectId, id: idFallback, ...body } = args;
      const resolvedId = projectId ?? idFallback ?? '';
      return m.mutate({ id: resolvedId, data: body as AddProjectRepoBody }, opts);
    },
    mutateAsync: (args: { projectId: string; id?: string } & AddProjectRepoBody) => {
      const { projectId, id: idFallback, ...body } = args;
      const resolvedId = projectId ?? idFallback ?? '';
      return m.mutateAsync({ id: resolvedId, data: body as AddProjectRepoBody });
    },
  };
}

// --- useRemoveProjectRepo ---
// Pages: mutate({ projectId, repoId }) — id in generated = projectId
export function useRemoveProjectRepo() {
  const m = _useRemoveProjectRepo();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      args: { projectId?: string; id?: string; repoId: string },
      opts?: Parameters<typeof m.mutate>[1],
    ) => {
      const resolvedId = args.projectId ?? args.id ?? '';
      return m.mutate({ id: resolvedId, repoId: args.repoId }, opts);
    },
    mutateAsync: (args: { projectId?: string; id?: string; repoId: string }) => {
      const resolvedId = args.projectId ?? args.id ?? '';
      return m.mutateAsync({ id: resolvedId, repoId: args.repoId });
    },
  };
}

// --- useVerifyContributor ---
// Pages: mutate({ projectId: id, contributorId, employmentType })
export function useVerifyContributor() {
  const m = _useVerifyContributor();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      args: { projectId?: string; id?: string; contributorId: string } & VerifyContributorBody,
      opts?: Parameters<typeof m.mutate>[1],
    ) => {
      const { projectId, id: idFallback, contributorId, ...body } = args;
      const resolvedId = projectId ?? idFallback ?? '';
      return m.mutate({ id: resolvedId, contributorId, data: body as VerifyContributorBody }, opts);
    },
    mutateAsync: (
      args: { projectId?: string; id?: string; contributorId: string } & VerifyContributorBody,
    ) => {
      const { projectId, id: idFallback, contributorId, ...body } = args;
      const resolvedId = projectId ?? idFallback ?? '';
      return m.mutateAsync({ id: resolvedId, contributorId, data: body as VerifyContributorBody });
    },
  };
}

// --- useRegisterUser ---
// Pages: register({ tenantName }, opts?) — flat body
export function useRegisterUser() {
  const m = _useRegisterUser();
  const base = withTypedError(m);
  return {
    ...base,
    mutate: (
      body: { tenantName: string },
      opts?: Parameters<typeof m.mutate>[1],
    ) => m.mutate({ data: body } as Parameters<typeof m.mutate>[0], opts),
    mutateAsync: (body: { tenantName: string }) =>
      m.mutateAsync({ data: body } as Parameters<typeof m.mutateAsync>[0]),
  };
}
